import { Link } from "@tanstack/react-router";
import { useState } from "react";

type Status = { type: "success" | "error"; msg: string } | null;

export function ContactForm({ variant = "light" }: { variant?: "light" | "dark" }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const dark = variant === "dark";

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const formData = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      subject: String(fd.get("subject") ?? ""),
      message: String(fd.get("message") ?? ""),
    };

    try {
      const response = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        setStatus({ type: "success", msg: "Vielen Dank! Wir melden uns in Kürze." });
        form.reset();
      } else {
        setStatus({ type: "error", msg: "Fehler beim Senden." });
      }
    } catch {
      setStatus({ type: "error", msg: "Serverfehler. Bitte später erneut versuchen." });
    }

    setLoading(false);
  };

  const inputCls = dark
    ? "w-full border border-black/20 mt-2 bg-white px-4 py-2 text-dark placeholder:text-dark focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/40"
    : "w-full rounded-xl border border-border bg-background px-4 py-2 text-black focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/30";

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <label htmlFor="cf-name" className={`mb-2 block ${dark ? "text-white/90" : "text-black/70"}`}>Vor- und Nachname*</label>
      <input id="cf-name" required name="name" aria-label="Vor- und Nachname" className={`${inputCls} rounded-[50px]`} />
      <label htmlFor="cf-email" className={`mb-2 block ${dark ? "text-white/90" : "text-black/70"}`}>E-Mail-Adresse*</label>
      <input id="cf-email" required type="email" name="email" aria-label="E-Mail-Adresse" className={`${inputCls} rounded-[50px]`} />
      <label htmlFor="cf-phone" className={`mb-2 block ${dark ? "text-white/90" : "text-black/70"}`}>Telefon*</label>
      <input id="cf-phone" required name="phone" aria-label="Telefon" className={`${inputCls} rounded-[50px]`} />
      <label htmlFor="cf-subject" className={`mb-2 block ${dark ? "text-white/90" : "text-black/70"}`}>Betreff</label>
      <input id="cf-subject" name="subject" aria-label="Betreff" className={`${inputCls} rounded-[50px]`} />
      <label htmlFor="cf-message" className={`mb-2 block ${dark ? "text-white/90" : "text-black/70"}`}>Nachricht</label>
      <textarea id="cf-message" name="message" rows={5} aria-label="Nachricht" className={`${inputCls} rounded-[10px]`} />
      <p className={`text-xs ${dark ? "text-white/90" : "text-black"}`}>
        <input type="checkbox" required className="mr-2" id="terms" /> <label htmlFor="terms" className={`text-sm ${dark ? "text-white/90" : "text-black/70"}`}>
          Sie erklären sich damit einverstanden, dass Ihre Daten zur Bearbeitung Ihres Anliegens verwendet werden. Weitere Informationen finden Sie in der <Link to="/datenschutz" className="underline hover:no-underline">
            Datenschutzerklärung
          </Link>.
        </label>
      </p>
      {status && (
        <div
          role="status"
          aria-live="polite"
          className={`rounded-md px-4 py-3 text-sm font-medium ${
            status.type === "success"
              ? "bg-green-600/15 text-green-100 border border-green-500/40"
              : "bg-red-600/15 text-red-100 border border-red-500/40"
          } ${dark ? "" : "!text-foreground"}`}
        >
          {status.msg}
        </div>
      )}
      <button
        type="submit"
        disabled={loading}
        className="w-fit rounded-full bg-brand-orange px-6 py-3 text-sm font-bold uppercase tracking-wider text-brand-orange-foreground transition-all hover:scale-[1.02] hover:bg-brand-orange/90 disabled:opacity-60"
      >
        {loading ? "Wird gesendet..." : "Anfrage senden"}
      </button>
    </form>
  );
}
